import React, { useState } from 'react';

const MONTHS_TR = ['Ocak','Şubat','Mart','Nisan','Mayıs','Haziran','Temmuz','Ağustos','Eylül','Ekim','Kasım','Aralık'];
const MONTHS_EN = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAYS_TR = ['Pt','Sa','Ça','Pe','Cu','Ct','Pz'];
const DAYS_EN = ['Mo','Tu','We','Th','Fr','Sa','Su'];

/**
 * CustomCalendar - Fully inline-styled date picker, no Tailwind CSS dependency
 * Props:
 *   selected: Date | null
 *   onSelect: (Date) => void
 *   disabled: (Date) => boolean
 *   language: 'tr' | 'en'
 *   minDate: Date (optional, defaults to today)
 */
export default function CustomCalendar({ selected, onSelect, disabled, language = 'tr', minDate }) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const [viewDate, setViewDate] = useState(() => {
    const d = selected ? new Date(selected) : new Date();
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d;
  });

  const months = language === 'tr' ? MONTHS_TR : MONTHS_EN;
  const days = language === 'tr' ? DAYS_TR : DAYS_EN;

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();

  // First day of month (0=Sunday, 1=Mon...)
  const firstDay = new Date(year, month, 1).getDay();
  // Days in month
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  // Offset: make Monday first (0=Mon, 6=Sun)
  const startOffset = firstDay === 0 ? 6 : firstDay - 1;

  const prevMonth = () => {
    const d = new Date(viewDate);
    d.setMonth(d.getMonth() - 1);
    setViewDate(d);
  };

  const nextMonth = () => {
    const d = new Date(viewDate);
    d.setMonth(d.getMonth() + 1);
    setViewDate(d);
  };

  const isDisabled = (day) => {
    const d = new Date(year, month, day);
    d.setHours(0, 0, 0, 0);
    if (disabled && disabled(d)) return true;
    return false;
  };

  const isToday = (day) => {
    const d = new Date(year, month, day);
    return d.toDateString() === today.toDateString();
  };

  const isSelected = (day) => {
    if (!selected) return false;
    const d = new Date(year, month, day);
    return d.toDateString() === new Date(selected).toDateString();
  };

  const handleDayClick = (day) => {
    if (isDisabled(day)) return;
    const d = new Date(year, month, day);
    d.setHours(0, 0, 0, 0);
    onSelect(d);
  };

  // Build grid cells
  const totalCells = Math.ceil((startOffset + daysInMonth) / 7) * 7;
  const cells = Array.from({ length: totalCells }, (_, i) => {
    const dayNum = i - startOffset + 1;
    return dayNum >= 1 && dayNum <= daysInMonth ? dayNum : null;
  });

  return (
    <div style={{ background: 'white', userSelect: 'none', padding: '14px', borderRadius: '12px', minWidth: '260px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
        <button
          type="button"
          onClick={prevMonth}
          style={{ width: '30px', height: '30px', borderRadius: '8px', border: '1px solid #D8D1C6', background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', color: '#1F3A2E', lineHeight: 1 }}
        >
          ‹
        </button>
        <span style={{ fontSize: '14px', fontWeight: 700, color: '#1F3A2E', fontFamily: 'Manrope, sans-serif' }}>
          {months[month]} {year}
        </span>
        <button
          type="button"
          onClick={nextMonth}
          style={{ width: '30px', height: '30px', borderRadius: '8px', border: '1px solid #D8D1C6', background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', color: '#1F3A2E', lineHeight: 1 }}
        >
          ›
        </button>
      </div>

      {/* Day headers */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: '4px' }}>
        {days.map(d => (
          <div key={d} style={{ textAlign: 'center', fontSize: '11px', fontWeight: 700, color: '#6E7F5B', padding: '4px 0', fontFamily: 'Manrope, sans-serif' }}>
            {d}
          </div>
        ))}
      </div>

      {/* Date grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
        {cells.map((day, idx) => {
          if (!day) return <div key={idx} />;

          const sel = isSelected(day);
          const tod = isToday(day);
          const dis = isDisabled(day);

          let bg = 'transparent';
          let color = '#1F1E1B';
          let border = '1.5px solid transparent';
          let cursor = 'pointer';
          let opacity = 1;

          if (sel) {
            bg = '#1F3A2E';
            color = 'white';
          } else if (tod) {
            border = '1.5px solid #1F3A2E';
            color = '#1F3A2E';
            bg = 'rgba(31,58,46,0.06)';
          } else if (dis) {
            color = '#C8C4BC';
            cursor = 'not-allowed';
            opacity = 0.5;
          }

          return (
            <button
              key={idx}
              type="button"
              onClick={() => handleDayClick(day)}
              disabled={dis}
              style={{
                width: '100%', aspectRatio: '1', borderRadius: '8px', border, background: bg, color,
                fontSize: '13px', fontWeight: sel || tod ? 700 : 400,
                cursor, opacity, fontFamily: 'Manrope, sans-serif',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'background 0.1s',
              }}
              onMouseEnter={e => { if (!sel && !dis) e.currentTarget.style.background = '#EFE7DA'; }}
              onMouseLeave={e => { if (!sel && !dis) e.currentTarget.style.background = bg; }}
            >
              {day}
            </button>
          );
        })}
      </div>
    </div>
  );
}
